import { Injectable } from '@angular/core';
import { LocalStorageKey, SegmentEvent, User } from '@app/shared/interfaces';
import { environment } from '@env/environment';
import { Logger } from '@utils';

export { SegmentEvent as SegmentEvent };

interface SegmentAnalytics {
  identify: (userId: string, traits: Record<string, unknown>, options: Record<string, unknown>) => void;
  page: (url: string, properties: Record<string, unknown>, options: Record<string, unknown>) => void;
  track: (event: string, properties: Record<string, unknown>) => void;
  reset: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class SegmentIoService {
  constructor() {}

  isProd() {
    if (environment.production && localStorage.getItem('isCS') !== 'true') {
      return true;
    } else {
      return false;
    }
  }

  identify(userId: string, payload: Partial<User>, additionalPayload?: Record<string, unknown>) {
    if (!this.isProd()) {
      return;
    }

    const featureFlagsStringified = localStorage.getItem(LocalStorageKey.featureFlags);

    if (featureFlagsStringified) {
      const featureFlags = JSON.parse(featureFlagsStringified);

      if (featureFlags.length > 0) {
        featureFlags.forEach((flag) => {
          payload['feature_' + flag] = '1';
        });
      }
    }

    try {
      const featureFlagsStringifiedPostHog = localStorage.getItem(LocalStorageKey.featureFlagsPostHog);

      if (featureFlagsStringifiedPostHog) {
        const featureFlags: string[] | null = JSON.parse(featureFlagsStringifiedPostHog);

        if (Array.isArray(featureFlags) && featureFlags.length > 0) {
          featureFlags.forEach((flag) => {
            // A system-level flag that we don't control and don't want to send to Segment/Intercom.
            if (flag.startsWith('survey-targeting')) {
              return;
            }
            payload['feature_' + flag] = '1';
          });
        }
      }
    } catch (err) {
      console.error(err);
    }

    if (payload.created_at) {
      payload.createdAt = payload.created_at;
    }

    // Map out the `priority_support` key for now as Chameleon does not gracefully handle differences in backend vs frontend identify calls sending different types
    if ('priority_support' in payload) {
      delete payload.priority_support;
    }

    const identifyExtras: Record<string, unknown> = {
      Intercom: {
        user_hash: payload.intercom_hash ? payload.intercom_hash : 'missing',
      },
    };

    // Add Chameleon hash if available for security encoding
    if (payload.chameleon?.hash) {
      identifyExtras.Chameleon = {
        uid_hash: payload.chameleon.hash,
      };
    } else if (payload.chameleon) {
      Logger.error('Chameleon hash missing from user object - this is required for security');
    }

    const identifyPayload = {
      ...payload,
      ...additionalPayload,
    };

    if (payload.team?.id) {
      identifyPayload['active_team_id'] = payload.team.id;
    }

    const analytics = (window as { analytics?: SegmentAnalytics }).analytics;
    if (analytics) {
      analytics.identify(userId, identifyPayload, identifyExtras);
    }
  }

  page(url: string) {
    if (!this.isProd()) {
      return;
    }

    const analytics = (window as { analytics?: SegmentAnalytics }).analytics;
    if (analytics) {
      analytics.page(url, {}, {});
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track(action: SegmentEvent, payload: any) {
    try {
      if (!this.isProd()) {
        Logger.info(`[Segment Event Preview]: ${action}`);
        return;
      }
      Logger.info(`[Segment Event]: ${action}`);

      const analytics = (window as { analytics?: SegmentAnalytics }).analytics;
      if (analytics) {
        analytics.track(action, payload);
      }
    } catch (err) {
      console.error(err);
    }
  }

  logout() {
    if (!this.isProd()) {
      return;
    }
    if ((window as { Intercom?: unknown }).Intercom) {
      ((window as { Intercom?: { (command: string): void } }).Intercom as (command: string) => void)('shutdown');
    }

    const analytics = (window as { analytics?: SegmentAnalytics }).analytics;
    if (analytics) {
      analytics.reset();
    }
  }
}
